/*
    ProgramsPage.jsx
    事業紹介ページ - ぐれいぷ & あそびコモンズ
*/

import React, { useState } from "react";
import { useCms, toParagraphs } from "../cms/CmsProvider";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheck, FaClock, FaYenSign, FaArrowRight, FaMapMarkerAlt, FaBicycle, FaCar } from "react-icons/fa";
import heroImage from '../assets/img/services3.jpg';
import scheduleImage from '../assets/img/services2.jpg';
import asobiImage from '../assets/img/services4.jpg';
import grapeLogo from '../assets/img/logo/grapelogo.png';
import grapeFacility1 from '../assets/img/grape-facility1.jpg';
import grapeFacility2 from '../assets/img/grape-facility2.png';

export default function ProgramsPage() {
    const { t, raw, text, image, list, items, site } = useCms("programs");

    // スケジュール：microCMS では「10:00 受付・自由遊び」のように1行ずつ入力
    const defaultTimes = ["10:00", "10:20", "10:30", "10:40", "11:10", "11:30", "12:00"];
    const cmsSchedule = list("schedule", []).map((line) => {
        const m = line.match(/^(\S+)\s+(.+)$/);
        return m ? { time: m[1], activity: m[2] } : { time: "", activity: line };
    });
    const schedule = cmsSchedule.length > 0
        ? cmsSchedule
        : defaultTimes.map((time, i) => ({ time, activity: t(`programsPage.grape.schedule.item${i + 1}`) }));

    const grapeFeatures = items("grapeFeatures", [1, 2, 3, 4].map((n) => ({
        title: t(`programsPage.grape.features.feature${n}.title`),
        description: t(`programsPage.grape.features.feature${n}.description`),
    })));
    const pointLabel = (i) => (i < 4 ? t(`programsPage.grape.features.point${i + 1}`) : `ポイント${i + 1}`);

    const facilityTexts = toParagraphs(raw("facilityDescription"));
    const facilityDescription = facilityTexts.length > 0
        ? facilityTexts
        : [1, 2, 3, 4].map((n) => t(`programsPage.grape.facility.description${n}`));

    const priceCards = items("priceCards", [
        { title: t("programsPage.pricing.enrollment.title"), price1: "¥6,000", note: t("programsPage.pricing.enrollment.note") + "\n\n" + t("programsPage.pricing.enrollment.noteExtra") },
        { title: t("programsPage.pricing.grape.title"), label1: t("programsPage.pricing.grape.plan1"), price1: "¥4,000", note: t("programsPage.pricing.grape.note") },
        { title: t("programsPage.pricing.asobi.title"), label1: t("programsPage.pricing.asobi.plan"), price1: "¥1,500" },
    ]);
    const cardClasses = ["enrollment", "grape-pricing", "asobi-pricing"];

    const asobiFeatures = list("asobiFeatures", [1, 2, 3].map((n) => `programsPage.asobi.features.item${n}`));
    const asobiValues = list("asobiValues", [1, 2, 3].map((n) => `programsPage.asobi.values.item${n}`));
    const priceNotes = list("priceNotes", ["includes", "extra"].map((k) => `programsPage.pricing.notes.${k}`));
    const trialFormUrl = site.trialFormUrl || "https://forms.gle/5aPN5YjJRY7h7Koe8";
    const [activeTab, setActiveTab] = useState('grape');

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.15, duration: 0.5 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
    };

    return (
        <div className="programs-page">
            {/* Hero Image */}
            <section className="programs-hero">
                <img src={image("heroImage", heroImage)} alt="教室の様子" className="programs-hero-image" />
            </section>

            {/* Tab Navigation */}
            <div className="programs-tabs">
                <button
                    className={`tab-button ${activeTab === 'grape' ? 'active' : ''}`}
                    onClick={() => setActiveTab('grape')}
                >
                    {text("grapeTabLabel", "programsPage.tabs.grape")}
                </button>
                <button
                    className={`tab-button ${activeTab === 'asobi' ? 'active' : ''}`}
                    onClick={() => setActiveTab('asobi')}
                >
                    {text("asobiTabLabel", "programsPage.tabs.asobi")}
                </button>
            </div>

            <AnimatePresence mode="wait">
                {activeTab === 'grape' && (
                    <motion.section
                        key="grape"
                        className="program-content grape-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >

                        <motion.div className="program-title-with-logo" variants={itemVariants}>
                            <img src={image("grapeLogo", grapeLogo)} alt="ぐれいぷ" className="grape-logo" />
                            <h2>{text("grapeTitle", "programsPage.grape.title")}</h2>
                        </motion.div>

                        <motion.div className="schedule-image-row" variants={itemVariants}>
                            <div className="program-schedule">
                                <h3><FaClock /> {text("scheduleTitle", "programsPage.grape.schedule.title")}</h3>
                                <p className="schedule-note">{text("scheduleNote", "programsPage.grape.schedule.afternoonNote")}</p>
                                <div className="schedule-table">
                                    {schedule.map((row, i) => (
                                        <div className="schedule-row" key={i}>
                                            <span className="time">{row.time}</span>
                                            <span className="activity">{row.activity}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="schedule-image-container">
                                <img src={image("scheduleImage", scheduleImage)} alt="活動の様子" className="schedule-image" />
                            </div>
                        </motion.div>

                        <motion.div className="program-intro" variants={itemVariants}>
                            <p>{text("grapeDescription", "programsPage.grape.description")}</p>
                        </motion.div>

                        <motion.div className="program-features" variants={itemVariants}>
                            <h3>{text("grapeFeaturesTitle", "programsPage.grape.features.title")}</h3>
                            <div className="features-list-vertical">
                                {grapeFeatures.map((f, i) => (
                                    <div className="feature-item" key={i}>
                                        <h4>{pointLabel(i)}　{f.title}</h4>
                                        <p>{f.description}</p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Goal */}
                        <motion.div className="program-goal" variants={itemVariants}>
                            <h3>{text("goalTitle", "programsPage.grape.goal.title")}</h3>
                            <p>{text("goalDescription", "programsPage.grape.goal.description")}</p>
                        </motion.div>

                        {/* Facility Info & Photos */}
                        <motion.div className="facility-section" variants={itemVariants}>
                            <div className="facility-photos">
                                <img src={image("facilityImage1", grapeFacility1)} alt="ぐれいぷ活動場所" className="facility-img" />
                                <img src={image("facilityImage2", grapeFacility2)} alt="浜の町公園" className="facility-img" />
                            </div>
                            <div className="facility-description">
                                {facilityDescription.map((p, i) => (
                                    <p key={i} style={{ whiteSpace: "pre-line" }}>{p}</p>
                                ))}
                            </div>
                            <div className="facility-cta">
                                <p>{text("facilityCta", "programsPage.grape.facility.cta")}</p>
                            </div>
                            <div className="facility-location">
                                <h4><FaMapMarkerAlt /> {text("facilityName", "programsPage.grape.facility.locationTitle")}</h4>
                                <p>{text("facilityAddress", "programsPage.grape.facility.address")}</p>
                                <div className="facility-access">
                                    <span><FaBicycle /> {text("facilityBicycle", "programsPage.grape.facility.bicycle")}</span>
                                    <span><FaCar /> {text("facilityParking", "programsPage.grape.facility.parking")}</span>
                                </div>
                            </div>
                        </motion.div>
                    </motion.section>
                )}

                {activeTab === 'asobi' && (
                    <motion.section
                        key="asobi"
                        className="program-content asobi-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <motion.div className="program-intro" variants={itemVariants}>
                            <h2>{text("asobiTitle", "programsPage.asobi.title")}</h2>
                            <p>{text("asobiDescription", "programsPage.asobi.description")}</p>
                        </motion.div>

                        <motion.div className="asobi-features-row" variants={itemVariants}>
                            <div className="program-features">
                                <h3>{text("asobiFeaturesTitle", "programsPage.asobi.features.title")}</h3>
                                <ul className="features-list">
                                    {asobiFeatures.map((item, i) => (
                                        <li key={i}><FaCheck /> {item}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="asobi-image-container">
                                <img src={image("asobiImage", asobiImage)} alt="野外活動" className="asobi-image" />
                            </div>
                        </motion.div>

                        <motion.div className="program-values" variants={itemVariants}>
                            <h3>{text("asobiValuesTitle", "programsPage.asobi.values.title")}</h3>
                            <ul className="values-list">
                                {asobiValues.map((item, i) => (
                                    <li key={i}>{item}</li>
                                ))}
                            </ul>
                            <p className="values-note">{text("asobiNote", "programsPage.asobi.values.note")}</p>
                        </motion.div>
                    </motion.section>
                )}
            </AnimatePresence>

            {/* Pricing Section */}
            <motion.section
                className="pricing-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.2 }}
            >
                <motion.h2 variants={itemVariants}>
                    <FaYenSign /> {text("pricingTitle", "programsPage.pricing.title")}
                </motion.h2>

                <motion.div className="pricing-cards" variants={itemVariants}>
                    {priceCards.map((card, i) => (
                        <div className={`pricing-card ${cardClasses[i] || ""}`} key={i}>
                            <h3>{card.title}</h3>
                            {[[card.label1, card.price1], [card.label2, card.price2], [card.label3, card.price3]]
                                .filter(([, price]) => price)
                                .map(([label, price], j) =>
                                    label ? (
                                        <div className="price-row" key={j}>
                                            <span>{label}</span>
                                            <span className="price">{price}</span>
                                        </div>
                                    ) : (
                                        <div className="price" key={j}>{price}</div>
                                    )
                                )}
                            {toParagraphs(card.note).map((note, j) => (
                                <p key={j} className={i === 0 ? undefined : "pricing-card-note"}>{note}</p>
                            ))}
                        </div>
                    ))}
                </motion.div>

                <motion.div className="pricing-notes" variants={itemVariants}>
                    <ul>
                        {priceNotes.map((note, i) => (
                            <li key={i}>{note}</li>
                        ))}
                    </ul>
                </motion.div>

                <motion.div className="member-benefits" variants={itemVariants}>
                    <h3>{text("benefitsTitle", "programsPage.pricing.benefits.title")}</h3>
                    <p>{text("benefitsDescription", "programsPage.pricing.benefits.description")}</p>
                </motion.div>
            </motion.section>

            {/* CTA Section */}
            <motion.section
                className="trial-cta-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.3 }}
            >
                <motion.div className="trial-cta-content" variants={itemVariants}>
                    <h2>{text("trialTitle", "programsPage.trial.title")}</h2>
                    <a
                        href={trialFormUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="trial-cta-button"
                    >
                        {text("trialButton", "programsPage.trial.button")} <FaArrowRight />
                    </a>
                </motion.div>
            </motion.section>
        </div>
    );
}
